/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Box,
  Button,
  Fade,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { cloneDeep } from "lodash";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  FulfilmentFAQsText,
  TermsAndConditionsText,
} from "../../../../components/layout/TermsAndConditionsText";
import MaterialSymbol from "../../../../components/MaterialSymbol";
import Modal from "../../../../components/Modal";
import {
  BoldWrapper,
  OrderLocationButton,
  PadButton,
  PadContent,
  PadIcon,
  WrapperA,
} from "../../../../components/Pad/Pad";
import { usePermission } from "../../../../hooks/usePermission";
import TestIds from "../../../../testIds";
import { URLS } from "../../../../util/consts";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";
import {
  ButtonBox,
  Container,
  GapContainer,
  InlineFlex,
  ReferenceNoteWrapper,
  Textarea,
} from "../../../cart/components/CartSummaryComponents";
import { usePickUpStoreComponents } from "../../../cart/components/PickUpStoreModal";
import { CartDeliveryMode } from "../../../cart/storage/cart.types";
import { CartMessage } from "../../../cart/utils/cartMessages";
import { selectStoreById } from "../../../stores/storage/stores.slice";
import { Quote, QuoteStatus } from "../../storage/types";
import { isQuoteStatusRequested } from "../../utils/getQuoteStatus";

import { QuoteCancelModal } from "./QuoteCancelModal";
import { QuoteDeclineModal } from "./QuoteDeclineModal";

const StyledUnderlineButton = styled(Button)(() => ({
  fontWeight: "bold",
  width: "100%",
  padding: "12px 0",
}));

interface QuoteSummaryProps extends PropsWithChildren {
  quote: Quote;
  setQuote: (quote: Quote) => void;
  note: string | null;
  setNote: (note: string) => void;
  handleContinue: () => void;
  onSend: () => void;
  submitting: boolean;
}

export const QuoteSummary = ({
  quote,
  setQuote,
  handleContinue,
  note,
  setNote,
  children,
  onSend,
  submitting,
}: QuoteSummaryProps) => {
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const canAcceptOrDeclineOffer = usePermission("Quotes_QuoteAcceptDecline").allowed;
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const account = useSelector(selectCurrentFarmerAccount);
  const isTfo = account?.hasTfoAccess || false;

  const pickupStore = useSelector(selectStoreById(quote.delivery.pickupStoreId ?? undefined));

  const { container, footer } = usePickUpStoreComponents(
    {
      cartDeliveryMode: pickupStore ? CartDeliveryMode.PICKUP : CartDeliveryMode.DELIVERY,
      deliveryDetails: quote.delivery.deliveryAddress ?? undefined,
      pickupStore,
      addressSettings: {},
    },
    (state) => {
      if (state.pickupStore) {
        quote.delivery.pickupStoreId = state.pickupStore.inventoryLocationId;
        quote.delivery.deliveryAddress = null;
        quote.delivery.deliveryInstructions = null;
        quote.delivery.dairySupplyNumber = null;

        setQuote(cloneDeep(quote));
      }
    },
    (state) => {
      quote.delivery.pickupStoreId = null;
      const deliveryDetails = state.delivery.deliveryDetails;
      quote.delivery.deliveryAddress = {
        streetAddress: deliveryDetails.streetAddress,
        name: deliveryDetails.addressName,
        city: deliveryDetails.city,
        postcode: deliveryDetails.postCode,
        recordId: deliveryDetails.recordId,
      };
      quote.delivery.deliveryInstructions = deliveryDetails.deliveryInstructions ?? null;
      quote.delivery.dairySupplyNumber = deliveryDetails.dairySupplyNumber ?? null;
      quote.delivery.isUrgent = deliveryDetails.urgent ?? null;
      setQuote(cloneDeep(quote));
    },
    () => {
      setIsModalOpen(false);
    }
  );

  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isErrorOnPickupStore, setIsErrorOnPickupStore] = React.useState<boolean>(false);
  const [buttonBoxHeight] = useState(80);

  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const renderEditPickUpStoreButton = React.useMemo(() => {
    const handleOpenPickUpStoreModal = () => {
      setIsModalOpen(true);
    };
    if (pickupStore) {
      return (
        <OrderLocationButton
          onClick={handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.quotes.summary.pickUpButton}
          icon="location_on"
          title="Pickup"
          subtitle={pickupStore.orgUnitName}
          isEditEnabled
        />
      );
    }
    if (quote.delivery.deliveryAddress) {
      const address = quote.delivery.deliveryAddress;
      const subtitle = `${address.streetAddress} ${address.city} ${address.postcode}`;
      return (
        <OrderLocationButton
          onClick={handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.quotes.summary.pickUpButton}
          icon="local_shipping"
          title="Delivery"
          subtitle={subtitle}
          isEditEnabled
        />
      );
    }
    return (
      <PadButton
        shade="light"
        sx={{ justifyContent: "flex-start", p: 1 }}
        onClick={handleOpenPickUpStoreModal}
        data-testid={TestIds.feat.cart.summary.pickUpButton}
        aria-invalid={isErrorOnPickupStore}
        aria-errormessage={isErrorOnPickupStore ? "pickup-error" : undefined}
        errorMessage={isErrorOnPickupStore ? "Pickup store required" : undefined}
      >
        <WrapperA>
          <BoldWrapper>
            <PadIcon className="material-symbols-outlined">add</PadIcon>
            <PadContent>Select pickup store or delivery</PadContent>
          </BoldWrapper>
        </WrapperA>
      </PadButton>
    );
  }, [container, isErrorOnPickupStore, setIsModalOpen]);

  const scrollToTop = () => {
    setTimeout(() => {
      const mc = document.getElementById("main-scroll-container");
      mc?.scrollTo({
        behavior: "smooth",
        top: mc.scrollHeight + 500,
        left: 0,
      });
    }, 100);
  };

  const handleAddNotes = useCallback(() => {
    setIsNotesOpen(true);
    scrollToTop();
  }, [setIsNotesOpen]);

  const renderAddNotes = useMemo(() => {
    const showButton = !isNotesOpen;

    if (showButton) {
      return (
        <PadButton
          shade="light"
          sx={{
            justifyContent: "flex-start",
            p: 1,
          }}
          onClick={handleAddNotes}
          data-testid={TestIds.feat.quotes.summary.addNotesButton}
        >
          <WrapperA>
            <BoldWrapper>
              <PadIcon className="material-symbols-outlined">add</PadIcon>
              <PadContent>Add customer notes</PadContent>
            </BoldWrapper>
          </WrapperA>
        </PadButton>
      );
    }
    return (
      <Fade in={!showButton}>
        <ReferenceNoteWrapper>
          <Textarea
            aria-label="Customer facing note for quote"
            label="Customer facing notes:"
            value={note ?? ""}
            onChange={(event) => {
              if (event.target.value.length <= 200 && currentAccount) {
                setNote(event.target.value);
              }
            }}
            multiline
            inputProps={{ maxLength: 200 }}
            minRows={4}
            variant="outlined"
            helperText={`${note ? note.length : 0}/200 characters`}
            testId={TestIds.feat.quotes.summary.notesField}
          />
        </ReferenceNoteWrapper>
      </Fade>
    );
  }, [note, setNote, isNotesOpen]);

  const onContinue = () => {
    if (!pickupStore && !quote.delivery.deliveryAddress) {
      setIsErrorOnPickupStore(true);
    } else {
      setIsErrorOnPickupStore(false);
      handleContinue();
    }
  };

  const onDecline = () => {
    setDeclineModalOpen(true);
  };

  const onCancel = () => {
    setCancelModalOpen(true);
  };

  const submitOrLoading = useMemo(() => {
    if (quote && currentAccount) {
      return (
        <Stack gap={"16px"}>
          {quote.status === QuoteStatus.Offered && canAcceptOrDeclineOffer && (
            <ButtonBox>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                onClick={onContinue}
                data-testid={TestIds.feat.quotes.summary.continueButton}
              >
                Continue to checkout
              </Button>
            </ButtonBox>
          )}
          {isTfo && quote?.status === QuoteStatus.Requested && (
            <ButtonBox>
              <Button
                variant="contained"
                sx={{ width: "100%" }}
                disabled={submitting}
                onClick={onSend}
                data-testid={TestIds.feat.quotes.summary.sendButton}
              >
                Send Quote
              </Button>
            </ButtonBox>
          )}
          {!isSm && quote?.status === QuoteStatus.Offered && canAcceptOrDeclineOffer && (
            <ButtonBox data-testid={TestIds.feat.quotes.summary.declineButton}>
              <StyledUnderlineButton variant="underline" onClick={onDecline}>
                Decline Quote
              </StyledUnderlineButton>
            </ButtonBox>
          )}
          {!isSm && quote?.status === QuoteStatus.Requested && (
            <ButtonBox data-testid={TestIds.feat.quotes.summary.cancelButton}>
              <StyledUnderlineButton variant="underline" onClick={onCancel}>
                Cancel Quote
              </StyledUnderlineButton>
            </ButtonBox>
          )}
        </Stack>
      );
    }
  }, [quote, currentAccount, canAcceptOrDeclineOffer, onContinue, onDecline, onCancel]);

  const mobileButtons = useMemo(() => {
    if (isSm && quote && currentAccount && canAcceptOrDeclineOffer) {
      return (
        <>
          {quote?.status === QuoteStatus.Offered && (
            <StyledUnderlineButton variant="underline" onClick={onDecline}>
              Decline Quote
            </StyledUnderlineButton>
          )}
          {quote?.status === QuoteStatus.Requested && (
            <StyledUnderlineButton variant="underline" onClick={onCancel}>
              Cancel Quote
            </StyledUnderlineButton>
          )}
        </>
      );
    }
  }, [quote, currentAccount, isSm, canAcceptOrDeclineOffer, onDecline, onCancel]);

  const userCanInteractWitQuote = useMemo(() => {
    return !isQuoteStatusRequested(quote);
  }, [quote.status]);

  return (
    <>
      <QuoteDeclineModal
        quote={quote}
        open={declineModalOpen}
        handleClose={() => setDeclineModalOpen(false)}
      />
      <QuoteCancelModal
        quote={quote}
        open={cancelModalOpen}
        handleClose={() => setCancelModalOpen(false)}
      />
      <Container buttonBoxHeight={buttonBoxHeight}>
        <GapContainer gap="1rem">
          {children}
          {userCanInteractWitQuote && (
            <>
              <InlineFlex>
                <Box component="span">
                  <Typography
                    variant="body2"
                    data-testid={TestIds.feat.quotes.summary.orderChargedMessage}
                  >
                    {CartMessage.chargedToAccount}
                  </Typography>
                </Box>
              </InlineFlex>
              <a href={URLS.termsAndConditions} target="_blank" rel="noreferrer">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
                  <TermsAndConditionsText />
                </Stack>
              </a>
              <a href={URLS.fulfilmentFAQs} target="_blank" rel="noreferrer">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
                  <FulfilmentFAQsText />
                </Stack>
              </a>
            </>
          )}
        </GapContainer>

        <GapContainer gap="1rem">
          {userCanInteractWitQuote && (
            <>
              <GapContainer>{renderEditPickUpStoreButton}</GapContainer>
              <GapContainer>{renderAddNotes}</GapContainer>
            </>
          )}
          {mobileButtons}
          {submitOrLoading}
        </GapContainer>

        <Modal
          title="Pickup or delivery?"
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          footer={footer}
          testId={TestIds.feat.pickUpStore.modal}
          maxWidth="md"
          paperProps={{
            sx: { height: isSm ? "inherit" : "844px" },
          }}
        >
          {container}
        </Modal>
      </Container>
    </>
  );
};

export default QuoteSummary;
